import React from 'react';
import { Globe, MapPin, ExternalLink, Navigation } from 'lucide-react';
import { GlobalOpportunityResult } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { BaseModal } from './BaseModal';

interface Props {
  careerTitle: string;
  data: GlobalOpportunityResult;
  onClose: () => void;
}

export const GlobalMapperModal: React.FC<Props> = ({ careerTitle, data, onClose }) => {
  const { theme } = useTheme();
  const fStyle = theme.colors.features.mapper;

  // Parse grounding chunks to find Maps data
  const mapLocations = data.chunks
    .map((c: any) => {
        const mapData = c.maps || c.web; 
        if (!mapData) return null;
        
        return {
            title: mapData.title || "Location",
            uri: mapData.googleMapsUri || mapData.uri,
            address: mapData.address
        };
    })
    .filter((loc): loc is { title: string; uri: string; address: any } => loc !== null && (!!loc.uri && loc.uri.includes('google.com/maps')));

  return (
    <BaseModal
        onClose={onClose}
        title={careerTitle}
        icon={<Globe size={16} />}
        headerColor={fStyle}
        maxWidth="max-w-4xl"
        footer={
             <div className={`text-center text-xs ${theme.colors.text.secondary}`}>
                 Powered by Google Maps Grounding
            </div>
        }
    >
        <div className="flex flex-col md:flex-row h-full">
            {/* AI Analysis Text */}
            <div className="flex-1 p-6 md:p-8 space-y-4">
                <h3 className={`text-lg font-bold flex items-center gap-2 ${theme.colors.text.main}`}>
                    <Navigation size={18} className={fStyle.icon} />
                    Strategic Hubs & Hotspots
                </h3>
                <div className={`prose prose-sm max-w-none leading-relaxed whitespace-pre-line ${theme.colors.text.secondary}`}>
                    <p>{data.text}</p>
                </div>
            </div>

            {/* Maps Sidebar */}
            <div className={`w-full md:w-80 border-l p-6 shrink-0 ${theme.mode === 'dark' ? 'bg-slate-850 border-slate-700' : 'bg-slate-50 border-gray-100'}`}>
                <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 ${theme.colors.text.secondary}`}>
                    Verified Locations
                </h4>
                
                {mapLocations.length > 0 ? (
                    <div className="space-y-3">
                        {mapLocations.map((loc, idx) => (
                            <a 
                                key={idx}
                                href={loc.uri}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`block p-3 rounded-xl border shadow-sm transition-all group ${
                                    theme.mode === 'dark' 
                                    ? 'bg-slate-800 border-slate-700 hover:border-blue-500' 
                                    : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'
                                }`}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex items-start gap-2">
                                        <MapPin size={16} className="text-red-500 mt-0.5 shrink-0" />
                                        <div>
                                            <div className={`font-bold text-sm transition-colors ${theme.colors.text.main} group-hover:text-blue-500`}>
                                                {loc.title}
                                            </div>
                                            {loc.address && (
                                                <div className={`text-xs mt-1 line-clamp-2 ${theme.colors.text.secondary}`}>
                                                    {loc.address}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <ExternalLink size={12} className={`${theme.colors.text.secondary} group-hover:text-blue-500`} />
                                </div>
                            </a>
                        ))}
                    </div>
                ) : (
                    <div className={`text-center py-8 text-sm italic ${theme.colors.text.secondary}`}>
                        No specific map links found, but check the analysis for city names.
                    </div>
                )}
            </div>
        </div>
    </BaseModal>
  );
};